import { useState, FormEvent } from "react";
import { ArrowRight, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const NewsletterBanner = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!valid || trimmed.length > 255) {
      toast({
        title: "Please enter a valid email",
        description: "We'll send occasional market updates — no spam.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setEmail("");
      toast({
        title: "You're on the list.",
        description: "Thank you — expect thoughtful updates, never noise.",
      });
    }, 600);
  };

  return (
    <section
      aria-label="Newsletter signup"
      className="relative border-y border-primary/15 bg-[hsl(34_22%_94%)] texture-paper"
    >
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
      />
      <div className="container mx-auto max-w-[1400px] px-6 py-10 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-10">
          <div className="flex items-start md:items-center gap-4 md:gap-5">
            <span className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-primary/25 text-primary">
              <Mail className="h-4 w-4" strokeWidth={1.5} />
            </span>
            <div>
              <p className="eyebrow text-primary mb-1.5">The Insider's List</p>
              <p className="font-serif text-lg md:text-xl text-foreground font-light leading-snug">
                Sign up for market updates, listings, and{" "}
                <span className="italic text-primary">expert real estate tips.</span>
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 sm:gap-0 w-full md:w-auto md:min-w-[440px]"
          >
            <label htmlFor="newsletter-email" className="sr-only">
              Email address
            </label>
            <input
              id="newsletter-email"
              type="email"
              required
              maxLength={255}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-background/70 border border-primary/20 sm:border-r-0 px-5 py-3.5 text-sm font-light text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 focus:bg-background transition-colors duration-300"
            />
            <button
              type="submit"
              disabled={submitting}
              className="group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-7 py-3.5 text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-primary/90 hover:shadow-[0_10px_28px_-12px_hsl(38_72%_62%/0.26)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 disabled:opacity-60"
            >
              {submitting ? "Joining…" : "Sign Up"}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default NewsletterBanner;
